/**
 * Capturing a receipt: photograph it or pick an image, and watch it upload.
 *
 * Three requests, in order (TECH_STACK.md §5): reserve a receipt and get a signed URL, PUT the
 * image straight to storage with progress, then tell the API it arrived. The image never passes
 * through our server. When the third request returns, the receipt is already `extracting`, and
 * the detail page takes over and polls it.
 *
 * Manual entry is offered on this screen, not hidden behind a failure, because it is a
 * first-class path (PRODUCT.md §11) -- and it is the one offered when the plan's receipt cap is
 * reached, since manual receipts do not count against the cap.
 */

import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Camera, ImageUp, PenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { UpgradeLink } from "@/features/billing/UpgradeLink";
import { Notice } from "@/components/ui/Field";
import { cn } from "@/lib/cn";
import { contentTypeOf, putWithProgress, UploadError } from "@/lib/upload";
import { useCompleteUpload, useStartUpload } from "./queries";

type Phase =
  | { kind: "idle" }
  | { kind: "uploading"; progress: number }
  | { kind: "finishing" }
  | { kind: "failed"; message: string }
  | { kind: "capReached"; cap: number };

export function CapturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const start = useStartUpload();
  const complete = useCompleteUpload();
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const busy = phase.kind === "uploading" || phase.kind === "finishing";

  async function upload(file: File) {
    setPhase({ kind: "uploading", progress: 0 });
    try {
      const reserved = await start.mutateAsync(contentTypeOf(file));
      await putWithProgress(reserved.upload_url, file, reserved.content_type, (progress) =>
        setPhase({ kind: "uploading", progress }),
      );
      setPhase({ kind: "finishing" });
      const receipt = await complete.mutateAsync(reserved.receipt_id);
      await navigate({ to: "/receipts/$receiptId", params: { receiptId: receipt.id } });
    } catch (error) {
      setPhase(failure(error));
    }
  }

  function failure(error: unknown): Phase {
    if (error instanceof ApiError && error.isReadOnly) {
      return { kind: "failed", message: t("common.readOnly") };
    }
    if (error instanceof ApiError && error.isEntitlement) {
      return { kind: "capReached", cap: Number(error.member("cap") ?? 0) };
    }
    if (error instanceof ApiError && error.code === "validation-failed") {
      return { kind: "failed", message: t("receipts.capture.unsupported") };
    }
    if (error instanceof UploadError) {
      return { kind: "failed", message: t("receipts.capture.uploadFailed") };
    }
    return { kind: "failed", message: t("receipts.capture.failed") };
  }

  function picked(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Cleared so that choosing the same file again after a failure still fires `change`.
    event.target.value = "";
    if (file) void upload(file);
  }

  return (
    <section className="space-y-5">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-stone-900">{t("receipts.capture.title")}</h1>
        <p className="text-sm text-stone-600">{t("receipts.capture.subtitle")}</p>
      </header>

      {/* Hidden inputs, opened by the buttons. `capture` asks a phone for the rear camera;
          a desktop browser ignores it and shows a file picker, which is fine. */}
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={picked}
      />
      <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={picked} />

      <div className="grid gap-3">
        <BigButton
          icon={<Camera className="h-6 w-6" aria-hidden />}
          label={t("receipts.capture.takePhoto")}
          primary
          disabled={busy}
          onClick={() => cameraInput.current?.click()}
        />
        <BigButton
          icon={<ImageUp className="h-6 w-6" aria-hidden />}
          label={t("receipts.capture.chooseImage")}
          disabled={busy}
          onClick={() => fileInput.current?.click()}
        />
      </div>

      {phase.kind === "uploading" && <Progress fraction={phase.progress} />}
      {phase.kind === "finishing" && <Notice tone="info">{t("receipts.capture.finishing")}</Notice>}
      {phase.kind === "failed" && <Notice tone="error">{phase.message}</Notice>}
      {phase.kind === "capReached" && (
        <Notice tone="info">
          {t("receipts.capture.capReached", { cap: phase.cap })} <UpgradeLink />
        </Notice>
      )}

      <Link
        to="/receipts/manual"
        className={cn(
          "flex min-h-11 items-center justify-center gap-2 rounded-lg text-base font-medium",
          phase.kind === "capReached"
            ? "bg-brand-700 hover:bg-brand-800 text-white"
            : "text-brand-700 hover:bg-brand-50",
        )}
      >
        <PenLine className="h-5 w-5" aria-hidden />
        {t("receipts.capture.enterManually")}
      </Link>
    </section>
  );
}

function BigButton({
  icon,
  label,
  primary = false,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  primary?: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex min-h-16 items-center justify-center gap-3 rounded-xl px-4 text-lg font-medium",
        "disabled:cursor-not-allowed disabled:opacity-60",
        primary
          ? "bg-brand-700 hover:bg-brand-800 text-white"
          : "border border-stone-300 bg-white text-stone-800 hover:bg-stone-50",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function Progress({ fraction }: { fraction: number }) {
  const { t } = useTranslation();
  const percent = Math.round(fraction * 100);
  return (
    <div className="space-y-1.5">
      <p className="text-sm text-stone-600">{t("receipts.capture.uploading", { percent })}</p>
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-2 overflow-hidden rounded-full bg-stone-200"
      >
        <div className="bg-brand-600 h-full transition-[width]" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
